import { fmtQty, longDate, money, taxBreakdown } from './invoices';

// Invoice PDF (US Letter), drawn as real text with pdf-lib so it stays sharp and small.
// Input: invoiceDocument() from utils/invoices.js. The logo (PNG/JPG/SVG/WebP) is drawn as an image.


// WinAnsi (the standard PDF fonts) can't encode everything; swap the common offenders.
const clean = (s) => String(s ?? '')
  .replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/[–—]/g, '-').replace(/…/g, '...').replace(/•/g, '-')
  .replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, '');

async function logoPng(url) {
  if (!url) return null;
  try {
    const blob = await (await fetch(url, { mode: 'cors' })).blob();
    const src = URL.createObjectURL(blob);
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = src;
      });
      const scale = 600 / Math.max(img.naturalWidth || 600, img.naturalHeight || 600);
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round((img.naturalWidth || 600) * scale));
      c.height = Math.max(1, Math.round((img.naturalHeight || 300) * scale));
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      const data = await new Promise((r) => c.toBlob(r, 'image/png'));
      return new Uint8Array(await data.arrayBuffer());
    } finally {
      URL.revokeObjectURL(src);
    }
  } catch {
    return null;
  }
}

export async function buildInvoicePdf(doc) {
  // Loaded on demand so pdf-lib stays out of the main bundle
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
  const INK = rgb(0.051, 0.106, 0.165);
  const MUTED = rgb(0.29, 0.353, 0.431);
  const LINE = rgb(0.839, 0.871, 0.91);
  const BLUE = rgb(0.118, 0.357, 0.722);
  const SOFT = rgb(0.957, 0.969, 0.98);
  const pdf = await PDFDocument.create();
  pdf.setTitle(`Invoice ${doc.number || 'draft'}`);
  pdf.setAuthor(clean(doc.seller.name));
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const W = 612;
  const H = 792;
  const M = 48;
  let page = pdf.addPage([W, H]);
  let y = H - M;

  const text = (s, x, yy, { size = 10, f = font, color = INK, align = 'left', maxW } = {}) => {
    let str = clean(s);
    if (maxW) while (str.length > 1 && f.widthOfTextAtSize(str, size) > maxW) str = str.slice(0, -1);
    const w = f.widthOfTextAtSize(str, size);
    const xx = align === 'right' ? x - w : align === 'center' ? x - w / 2 : x;
    page.drawText(str, { x: xx, y: yy, size, font: f, color });
  };
  const wrap = (s, f, size, maxW) => {
    const out = [];
    for (const para of clean(s).split(/\r?\n/)) {
      let lineStr = '';
      for (const word of para.split(/\s+/)) {
        const tryStr = lineStr ? `${lineStr} ${word}` : word;
        if (f.widthOfTextAtSize(tryStr, size) > maxW && lineStr) { out.push(lineStr); lineStr = word; } else lineStr = tryStr;
      }
      out.push(lineStr);
    }
    return out;
  };
  const block = (lines, x, yy, opts = {}) => {
    const size = opts.size || 10;
    for (const l of lines) { text(l, x, yy, { ...opts, size }); yy -= size + 4; }
    return yy;
  };
  const newPageIfNeeded = (need) => {
    if (y - need < M + 40) { page = pdf.addPage([W, H]); y = H - M; }
  };

  // Header: logo or name (left), INVOICE + number (right)
  const png = await logoPng(doc.seller.logo_url);
  if (png) {
    const img = await pdf.embedPng(png);
    const s = Math.min(170 / img.width, 56 / img.height);
    page.drawImage(img, { x: M, y: y - img.height * s + 6, width: img.width * s, height: img.height * s });
  } else {
    text(doc.seller.name, M, y - 14, { size: 18, f: bold });
  }
  text('INVOICE', W - M, y - 14, { size: 22, f: bold, align: 'right', color: BLUE });
  text(doc.number ? `# ${doc.number}` : 'DRAFT', W - M, y - 32, { size: 11, f: bold, align: 'right' });
  y -= 72;

  // From / Bill to / dates
  const sellerLines = [doc.seller.name, ...(doc.seller.address || '').split(/\r?\n/), doc.seller.phone, doc.seller.email, doc.seller.website,
    doc.seller.ein ? `EIN ${doc.seller.ein}` : null].filter(Boolean);
  const clientLines = [doc.client.name, doc.client.contact_name ? `Attn: ${doc.client.contact_name}` : null,
    ...(doc.client.address || '').split(/\r?\n/), doc.client.tax_exempt ? `Tax exempt${doc.client.tax_exempt_id ? ` #${doc.client.tax_exempt_id}` : ''}` : null].filter(Boolean);
  text('FROM', M, y, { size: 8, f: bold, color: MUTED });
  text('BILL TO', M + 190, y, { size: 8, f: bold, color: MUTED });
  const yA = block(sellerLines, M, y - 14, { size: 9.5, maxW: 180 });
  const yB = block(clientLines, M + 190, y - 14, { size: 9.5, maxW: 150 });
  const meta = [['Issue date', longDate(doc.issue_date)], ['Due date', longDate(doc.due_date)], ...(doc.po_number ? [['PO number', doc.po_number]] : [])];
  let yM = y;
  for (const [k, v] of meta) {
    text(k, W - M - 160, yM, { size: 8.5, color: MUTED });
    text(v, W - M, yM, { size: 9.5, f: bold, align: 'right' });
    yM -= 16;
  }
  page.drawRectangle({ x: W - M - 160, y: yM - 30, width: 160, height: 34, color: SOFT });
  text('Amount due', W - M - 150, yM - 17, { size: 8.5, color: MUTED });
  text(money(doc.total_cents - doc.paid_cents), W - M - 8, yM - 18, { size: 13, f: bold, align: 'right' });
  y = Math.min(yA, yB, yM - 40) - 18;

  // Lines table
  const cols = { desc: M, qty: W - M - 210, price: W - M - 110, amount: W - M };
  const header = () => {
    page.drawRectangle({ x: M, y: y - 6, width: W - 2 * M, height: 20, color: SOFT });
    text('DESCRIPTION', cols.desc + 6, y, { size: 8, f: bold, color: MUTED });
    text('QTY', cols.qty, y, { size: 8, f: bold, color: MUTED, align: 'right' });
    text('PRICE', cols.price, y, { size: 8, f: bold, color: MUTED, align: 'right' });
    text('AMOUNT', cols.amount - 12, y, { size: 8, f: bold, color: MUTED, align: 'right' });
    y -= 22;
  };
  header();
  for (const l of doc.lines) {
    const descLines = wrap(l.description || '', font, 9.5, cols.qty - cols.desc - 60);
    const need = descLines.length * 13.5 + 10;
    if (y - need < M + 40) { page = pdf.addPage([W, H]); y = H - M; header(); }
    descLines.forEach((d, i) => text(d, cols.desc + 6, y - i * 13.5, { size: 9.5 }));
    const qty = `${fmtQty(l.quantity)}${l.unit && l.unit !== 'each' ? ` ${l.unit}${Number(l.quantity) === 1 ? '' : 's'}` : ''}`;
    text(qty, cols.qty, y, { size: 9.5, align: 'right' });
    text(money(l.unit_price_cents), cols.price, y, { size: 9.5, align: 'right' });
    text(money(l.amount_cents), cols.amount - 12, y, { size: 9.5, align: 'right' });
    if (l.taxable && (l.taxes || []).length) text('T', cols.amount - 8, y, { size: 7, color: MUTED });
    y -= descLines.length * 13.5 + 6;
    page.drawLine({ start: { x: M, y: y + 2 }, end: { x: W - M, y: y + 2 }, thickness: 0.5, color: LINE });
    y -= 10;
  }

  // Totals
  newPageIfNeeded(140);
  const tx = W - M - 12;
  const row = (label, value, opts = {}) => {
    text(label, tx - 120, y, { size: opts.size || 9.5, f: opts.f || font, color: opts.color || MUTED, align: 'right' });
    text(value, tx, y, { size: opts.size || 9.5, f: opts.f || font, align: 'right' });
    y -= (opts.size || 9.5) + 7;
  };
  row('Subtotal', money(doc.subtotal_cents));
  if (doc.discount_cents) row('Discount', `-${money(doc.discount_cents)}`);
  for (const t of taxBreakdown(doc.lines)) row(`${t.name} (${t.rate}%)`, money(t.cents));
  row('Total', money(doc.total_cents), { f: bold, size: 11, color: INK });
  if (doc.paid_cents) row('Paid', `-${money(doc.paid_cents)}`);
  row('Amount due', money(doc.total_cents - doc.paid_cents), { f: bold, size: 12, color: BLUE });
  y -= 10;

  // Paying by check, notes, terms
  newPageIfNeeded(110);
  text('HOW TO PAY', M, y, { size: 8, f: bold, color: MUTED });
  y -= 15;
  y = block([
    `By check, payable to ${doc.seller.check_payable_to || doc.seller.name}`,
    ...(doc.seller.remit_address ? [`Mail to: ${(doc.seller.remit_address || '').split(/\r?\n/).filter(Boolean).join(', ')}`] : []),
    ...(doc.number ? [`Please write invoice ${doc.number} on the memo line.`] : []),
  ], M, y, { size: 9.5 });
  y -= 8;
  for (const [label, body] of [['NOTES', doc.notes], ['TERMS', doc.terms]]) {
    if (!body) continue;
    const ls = wrap(body, font, 9.5, W - 2 * M);
    newPageIfNeeded(ls.length * 13.5 + 24);
    text(label, M, y, { size: 8, f: bold, color: MUTED });
    y = block(ls, M, y - 15, { size: 9.5 }) - 8;
  }
  if (doc.seller.footer) {
    const ls = wrap(doc.seller.footer, font, 8.5, W - 2 * M);
    ls.forEach((l, i) => text(l, W / 2, M - 10 + (ls.length - 1 - i) * 12, { size: 8.5, color: MUTED, align: 'center' }));
  }
  return pdf.save();
}
